import { isModuleId, MODULES } from '@tahi/core';
import { useEffect, useState } from 'react';
import { Button } from '../../ui/Button';
import { adminApi, adminErrorText, type AuditRow } from './adminApi';

const LABELS: Record<string, string> = {
  'module_override.set': 'Module override',
  'invite.create': 'Invite added',
  'invite.revoke': 'Invite removed',
  'reset_link.create': 'Reset link created',
  'reset_link.use': 'Reset link used',
  'app_settings.update': 'App settings changed',
};

function show(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'object') return String(v);
  const entries = Object.entries(v as Record<string, unknown>);
  if (entries.length === 1) return String(entries[0]?.[1]);
  return entries.map(([k, x]) => `${k} ${String(x)}`).join(', ');
}

export function describeAudit(e: AuditRow, emailOf: (id: string | null) => string | null): string {
  const what = LABELS[e.action] ?? e.action;
  const mod =
    e.entityType === 'module' && e.entityId && isModuleId(e.entityId)
      ? ` · ${MODULES[e.entityId].name}`
      : '';
  const target = emailOf(e.targetUserId);
  const forWho = target ? ` for ${target}` : '';
  const change =
    e.before != null || e.after != null ? `: ${show(e.before)} → ${show(e.after)}` : '';
  return `${what}${mod}${forWho}${change}`;
}

const when = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' });

export function AuditPanel() {
  const [entries, setEntries] = useState<AuditRow[] | null>(null);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [emails, setEmails] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const emailOf = (id: string | null) => (id ? (emails.get(id) ?? null) : null);

  useEffect(() => {
    Promise.all([adminApi.listAudit(), adminApi.listUsers()]).then(
      ([page, users]) => {
        setEmails(new Map(users.map((u) => [u.id, u.email])));
        setEntries(page.entries);
        setNextBefore(page.nextBefore);
      },
      (err: unknown) => setError(adminErrorText(err)),
    );
  }, []);

  async function loadMore() {
    if (!nextBefore) return;
    try {
      const page = await adminApi.listAudit(nextBefore);
      setEntries((list) => [...(list ?? []), ...page.entries]);
      setNextBefore(page.nextBefore);
    } catch (err) {
      setError(adminErrorText(err));
    }
  }

  if (error)
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  if (!entries)
    return (
      <p role="status" className="text-muted">
        Loading the audit log…
      </p>
    );
  if (entries.length === 0) return <p className="text-muted">No admin changes yet.</p>;
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        {entries.map((e) => (
          <li key={e.id} className="rounded-2xl bg-surface-2 p-3">
            <p className="text-sm font-medium">{emailOf(e.actorId) ?? 'Unknown admin'}</p>
            <p className="text-sm">{describeAudit(e, emailOf)}</p>
            <p className="font-mono text-xs tabular-nums text-muted">
              {when.format(new Date(e.at))}
            </p>
          </li>
        ))}
      </ul>
      {nextBefore ? <Button onClick={() => void loadMore()}>Load more</Button> : null}
    </div>
  );
}
